import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { Reveal, RevealGroup, RevealItem } from "@/components/site/Reveal";
import { Newsletter } from "@/components/site/Newsletter";
import api from "@/lib/api";
import { cn } from "@/lib/utils";

const fmtDate = (d) => { try { return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); } catch { return d; } };

export default function Insights() {
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("All");
  useEffect(() => { api.getInsights().then(setItems).catch(() => {}); }, []);

  const cats = useMemo(() => ["All", ...Array.from(new Set(items.map((i) => i.category)))], [items]);
  const filtered = cat === "All" ? items : items.filter((i) => i.category === cat);
  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <div data-testid="insights-page">
      <PageHero
        breadcrumb={[{ label: "Insights" }]}
        eyebrow="Market intelligence"
        title="The cross-market view, written by practitioners."
        intro="Proprietary analysis of the Monaco-Geneva talent market — compensation, mobility, retention and the trends shaping private banking hiring."
      />

      <section className="section-py pt-6">
        <div className="container-bp">
          <Reveal>
            <div className="bp-scroll-x -mx-[var(--gutter)] flex gap-2 overflow-x-auto px-[var(--gutter)] pb-1 sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0">
              {cats.map((c) => (
                <button key={c} onClick={() => setCat(c)} data-testid={`insight-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                  className={cn("shrink-0 whitespace-nowrap rounded-full border px-4 py-2 text-[12.5px] font-medium transition-colors", cat === c ? "border-[color:var(--bp-teal)] bg-[color:var(--bp-teal)] text-[color:var(--bp-bg)]" : "border-[color:var(--bp-border)] text-[color:var(--bp-text-muted)] hover:border-[color:var(--bp-teal)] hover:text-[color:var(--bp-teal)]")}>
                  {c}
                </button>
              ))}
            </div>
          </Reveal>

          {featured && (
            <Reveal>
              <Link to={`/insights/${featured.slug}`} data-testid="insight-featured" className="group mt-8 grid grid-cols-1 overflow-hidden rounded-[var(--radius-lg)] border border-[color:var(--bp-border)] bg-[color:var(--bp-surface)] transition-all duration-300 hover:border-[color:var(--bp-teal)]/50 lg:grid-cols-2">
                <div className="relative aspect-[16/10] overflow-hidden lg:aspect-auto">
                  <img src={featured.image} alt={featured.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="flex flex-col justify-center p-8 lg:p-10">
                  <span className="eyebrow text-[color:var(--bp-teal)]">{featured.category}</span>
                  <h2 className="mt-3 font-display text-2xl font-semibold leading-snug text-[color:var(--bp-cream)] lg:text-3xl">{featured.title}</h2>
                  <p className="mt-3 text-[14.5px] leading-relaxed text-[color:var(--bp-text-muted)]">{featured.excerpt}</p>
                  <div className="mt-5 flex items-center gap-4 text-[12px] text-[color:var(--bp-text-muted)]">
                    <span>{fmtDate(featured.date)}</span><span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {featured.reading_time} min</span>
                  </div>
                  <span className="mt-5 inline-flex items-center gap-2 text-[13px] font-semibold text-[color:var(--bp-teal)]">Read the analysis <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
                </div>
              </Link>
            </Reveal>
          )}

          <RevealGroup className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-3">
            {rest.map((a) => (
              <RevealItem key={a.slug}>
                <Link to={`/insights/${a.slug}`} data-testid={`insight-card-${a.slug}`} className="group flex h-full flex-col overflow-hidden rounded-[var(--radius-lg)] border border-[color:var(--bp-border)] bg-[color:var(--bp-surface)] transition-all duration-300 hover:-translate-y-1 hover:border-[color:var(--bp-teal)]/50">
                  <div className="aspect-[16/10] overflow-hidden"><img src={a.image} alt={a.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /></div>
                  <div className="flex flex-1 flex-col p-6">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[color:var(--bp-teal)]">{a.category}</span>
                    <h3 className="mt-2 font-display text-lg font-semibold leading-snug text-[color:var(--bp-cream)]">{a.title}</h3>
                    <p className="mt-2 flex-1 text-[13.5px] leading-relaxed text-[color:var(--bp-text-muted)]">{a.excerpt}</p>
                    <div className="mt-4 flex items-center gap-3 text-[12px] text-[color:var(--bp-text-muted)]"><span>{fmtDate(a.date)}</span><span>·</span><span>{a.reading_time} min</span></div>
                  </div>
                </Link>
              </RevealItem>
            ))}
          </RevealGroup>
        </div>
      </section>

      <section className="section-py bg-[color:var(--bp-bg-2)]">
        <div className="container-bp">
          <div className="mx-auto max-w-2xl rounded-[var(--radius-lg)] border border-[color:var(--bp-border)] bg-[color:var(--bp-surface)] p-8 text-center sm:p-10">
            <p className="eyebrow inline-flex items-center justify-center gap-2 text-[color:var(--bp-gold)]"><span className="h-px w-8 bg-[color:var(--bp-gold)]" /> Quarterly intelligence</p>
            <h2 className="mt-5 font-display text-2xl font-semibold text-[color:var(--bp-cream)] sm:text-3xl">Get the Monaco-Geneva Talent Barometer</h2>
            <p className="mx-auto mt-3 max-w-md text-[14px] text-[color:var(--bp-text-muted)]">Compensation movement, hiring velocity and talent flow — delivered each quarter.</p>
            <div className="mx-auto mt-6 max-w-md"><Newsletter /></div>
          </div>
        </div>
      </section>
    </div>
  );
}
